// ChunkErrorBoundary — turns a failed dynamic-import (chunk 404 after a
// redeploy, or a dropped connection on flaky mobile) into a friendly
// "A new version is available — Reload" panel instead of the bare router
// "Oops" full-page error.
//
// Why this exists (#261, Gap 6): `lazyWithRetry` retries a rejected `import()`
// once, then re-throws the failure tagged `ChunkLoadError`. That tagged error
// would otherwise bubble to React Router's `errorElement` (ErrorView) — a dead
// end for what is really just "your tab is running last week's build." This
// boundary catches it one level above the lazy/Suspense surface and offers the
// one action that actually fixes it: a hard reload to fetch the current build.
//
// CRITICAL: it only handles CHUNK failures. A genuine bug thrown by a loaded
// component is NOT a chunk error — we deliberately re-throw it (by flipping
// state and throwing in render) so the existing router `errorElement` still
// surfaces the real stack. This boundary must never swallow real bugs.
//
// Uses a class component because React error boundaries require it —
// `componentDidCatch` / `getDerivedStateFromError` have no hook equivalent.
//
// Load-path only (#261): renders nothing on the happy path (just its children)
// and does no per-frame work — zero effect on the viewer/RTX render path.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '@/lib/lazyWithRetry';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
}

interface ChunkErrorBoundaryState {
  /** A confirmed chunk-load failure — render the reload panel. */
  chunkError: Error | null;
  /** A non-chunk error — re-throw in render so the router errorElement wins. */
  rethrow: Error | null;
}

export class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { chunkError: null, rethrow: null };

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    // Route the caught error: chunk failures get the reload panel; everything
    // else is staged for a re-throw so the real error boundary handles it.
    return isChunkLoadError(error)
      ? { chunkError: error, rethrow: null }
      : { chunkError: null, rethrow: error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log either way so the dev console still shows the failure and we can see
    // which surface caught it. Real bugs are additionally re-surfaced below.
    if (isChunkLoadError(error)) {
      console.warn('[ChunkErrorBoundary] chunk load failed', error.message);
    } else {
      console.error(
        '[ChunkErrorBoundary] non-chunk error',
        error,
        info.componentStack,
      );
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    // Non-chunk error: re-throw so the nearest router `errorElement`
    // (ErrorView) catches it. Throwing here — during render — is what lets the
    // error propagate past this boundary to the next one up.
    if (this.state.rethrow) {
      throw this.state.rethrow;
    }

    if (this.state.chunkError) {
      return (
        <div className="flex h-full min-h-[60vh] w-full flex-col items-center justify-center gap-4 bg-telekinesis-bg-secondary-dark px-6 py-10">
          <h1 className="text-xl font-semibold text-telekinesis-text-primary">
            A new version is available
          </h1>
          <p className="max-w-md text-center text-sm text-telekinesis-text-secondary">
            We couldn&apos;t load part of the app — this usually means a new
            version was just released. Reload to get the latest.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded-md border border-white/20 bg-black px-4 py-2 text-sm text-telekinesis-text-primary transition-colors hover:border-white/40 hover:bg-white/5"
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
